/**
 * The side panel of the redesign (§23): the step inspector and the RFC
 * statement editor (§45.8).
 *
 * Non modal — no scrim, the page stays usable — 400 wide on the right, from
 * under the app header (top 52) to the bottom, with the menu shadow and a
 * hairline on its left; full width under 1024. A sticky 52 header holds the
 * title (15/600, no « STEP N » kicker) and Close; the body scrolls on its own,
 * padding 20, and there is no footer: every field writes into the page's
 * draft, which the publish bar or the draft band says. On opening the focus
 * goes to the title; Escape (focus inside) or Close closes it, and the focus
 * goes back to what opened it. Under 1024, where it covers the page, it is
 * modal: Tab stays inside it (§23).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, shadow } from '../theme/tokens';
import { CloseMark } from './icons';
import { useFocusTrap } from '../lib/focus-trap';
import { useMotionMount } from '../lib/presence';

/** The width the page leaves free on its right while the panel is open (§45.8). */
export const SIDE_PANEL_WIDTH = 400;

export function SidePanel({
  id,
  title,
  closeLabel,
  onClose,
  children
}: {
  id: string;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const heading = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => heading.current?.focus(), [id]);
  // Entrance x 24→0 with o, d 1 r .4; exit o and x 24 in 120 ms (§35).
  const motion = useMotionMount<HTMLElement>({ x: 24, o: 0 }, { d: 1, r: 0.4 }, { o: 0, x: 24 });
  const box = useRef<HTMLElement | null>(null);
  const panelRef = useCallback(
    (node: HTMLElement | null): void => {
      box.current = node;
      motion(node);
    },
    [motion]
  );
  const covers = useCovers();
  useFocusTrap(box, covers);
  // Above 1024 the page stays usable, so nothing holds the focus; still, Close
  // gives it back to what opened the panel.
  useEffect(() => {
    if (covers) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      // Only if the focus was in the panel (or went with it), not if it was taken elsewhere on the page.
      const now = document.activeElement;
      if (opener?.isConnected && (!now || now === document.body || now.closest('.al-side-panel'))) opener.focus();
    };
  }, [covers]);

  return (
    <aside
      ref={panelRef}
      id={id}
      role={covers ? 'dialog' : undefined}
      aria-modal={covers ? true : undefined}
      aria-labelledby={`${id}-title`}
      className="al-side-panel"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose();
      }}
      style={{
        position: 'fixed',
        top: 52,
        right: 0,
        bottom: 0,
        zIndex: 30,
        width: SIDE_PANEL_WIDTH,
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        background: color.surface,
        boxShadow: `${shadow.menu}, inset 1px 0 0 ${color.line}`
      }}
    >
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 52,
          padding: '0 12px 0 20px',
          boxShadow: `inset 0 -1px 0 ${color.line}`
        }}
      >
        <h2
          id={`${id}-title`}
          ref={heading}
          tabIndex={-1}
          style={{
            flex: 1,
            minWidth: 0,
            margin: 0,
            fontSize: 15,
            fontWeight: 600,
            color: color.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            outline: 'none'
          }}
        >
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          style={{
            display: 'flex',
            width: 28,
            height: 28,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            border: 0,
            background: 'none',
            color: color.inkSubtle,
            cursor: 'pointer'
          }}
        >
          <CloseMark />
        </button>
      </div>
      <div className="nsb" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>
        {children}
      </div>
    </aside>
  );
}

/** Whether the panel covers the page (under 1024, §23), kept up to date as the window changes. */
function useCovers(): boolean {
  const query = '(max-width: 1023px)';
  const [covers, setCovers] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const change = (): void => setCovers(list.matches);
    list.addEventListener('change', change);
    return () => list.removeEventListener('change', change);
  }, []);
  return covers;
}
